import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../Redux/authSlice";

const NotificationsContext = createContext(null);

// Used by the focus/visibility safety-net handlers to avoid hammering the
// API. Real-time updates now flow through the SSE stream; polling is gone.
const MIN_REFETCH_GAP_MS = 5_000;

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5050").replace(/\/$/, "");

export const NotificationsProvider = ({ children }) => {
  // Auth gate: skip all notification work when no user is logged in.
  // This stops the SSE endpoint from being called on /login, public pages,
  // or after logout — which would otherwise produce a 401 retry loop.
  const user = useSelector(selectUser);
  const isLoggedIn = !!user;

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs prevent stale-closure issues and let us suppress hammering the API.
  const inFlightRef = useRef(null);
  const lastFetchAtRef = useRef(0);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async ({ force = false } = {}) => {
    if (inFlightRef.current) return inFlightRef.current;
    if (!force && Date.now() - lastFetchAtRef.current < MIN_REFETCH_GAP_MS) {
      return;
    }

    const ctrl = new AbortController();
    const promise = (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/notifications`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.success) {
          throw new Error(json?.message || `Request failed (${res.status})`);
        }

        if (!mountedRef.current) return;
        setNotifications(Array.isArray(json.data) ? json.data : []);
        setUnreadCount(Number.isFinite(json.unreadCount) ? json.unreadCount : 0);
        lastFetchAtRef.current = Date.now();
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!mountedRef.current) return;
        console.error("[notifications] fetch failed:", err.message);
        setError(err.message);
      } finally {
        if (mountedRef.current) setLoading(false);
        inFlightRef.current = null;
      }
    })();

    promise.cancel = () => ctrl.abort();
    inFlightRef.current = promise;
    return promise;
  }, []);

  // ---- Lifecycle: initial load + SSE push + focus/visibility safety net ----
  //
  // Re-runs whenever `isLoggedIn` flips (login/logout). On logout the previous
  // cleanup function closes the SSE connection and clears state, so the
  // browser doesn't keep retrying the /stream endpoint with no cookie.
  useEffect(() => {
    mountedRef.current = true;

    // Not logged in (or just logged out) — clear stale state and do nothing.
    // No fetch, no SSE, no event listeners. Endpoint is never called.
    if (!isLoggedIn) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }

    // 1. Initial hydration — fetch the current list when the provider mounts.
    fetchNotifications({ force: true });

    // 2. SSE: real-time push from backend.
    //    The browser opens a long-running GET to /api/notifications/stream.
    //    Cookies (and the auth middleware) work the same as every other route.
    //    EventSource handles reconnection automatically on disconnect.
    const es = new EventSource(`${API_BASE}/api/notifications/stream`, {
      withCredentials: true,
    });

    es.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg?.type === "notification.new") {
          fetchNotifications({ force: true });
        }
      } catch {
        /* ignore malformed payloads */
      }
    };

    es.onerror = () => {
      // EventSource will auto-reconnect — nothing to do here.
    };

    // 3. Safety net: catch up on missed events when the user returns to the
    //    tab. Real-time pushes already handle the happy path; these handlers
    //    cover the case where the stream dropped while disconnected.
    const onFocus = () => fetchNotifications();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchNotifications();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mountedRef.current = false;
      try { es.close(); } catch { /* ignore */ }
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      if (inFlightRef.current?.cancel) inFlightRef.current.cancel();
    };
  }, [isLoggedIn, fetchNotifications]);

  // ---- Optimistic mark-as-read for a single notification ----
  const markAsRead = useCallback(async (id) => {
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        if (!n.read) wasUnread = true;
        return { ...n, read: true };
      })
    );
    if (wasUnread) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }

    try {
      const res = await fetch(`${API_BASE}/api/notifications/${id}/read`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Mark-read failed (${res.status})`);
    } catch (err) {
      console.error("[notifications] markAsRead failed:", err.message);
      // Roll back optimistic update by refetching authoritative state.
      fetchNotifications({ force: true });
    }
  }, [fetchNotifications]);

  // ---- Optimistic mark-all-as-read ----
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      const res = await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Mark-all-read failed (${res.status})`);
    } catch (err) {
      console.error("[notifications] markAllRead failed:", err.message);
      fetchNotifications({ force: true });
    }
  }, [fetchNotifications]);

  const refresh = useCallback(() => fetchNotifications({ force: true }), [fetchNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      markAsRead,
      markAllRead,
      refresh,
    }),
    [notifications, unreadCount, loading, error, markAsRead, markAllRead, refresh]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within a <NotificationsProvider>");
  }
  return ctx;
};
